import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Button, Checkbox, ModuleHeader } from '@viapeople/components'
import { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import { useToast } from '../../utils/ToastContext'
import { trpc } from '../../utils/trpc'
import { CalibrationError } from './components/CalibrationError.tsx'
import { CalibrationLoading } from './components/CalibrationLoading.tsx'
import { CalibrationNotFound } from './components/CalibrationNotFound.tsx'
import { CalibrationOverralCommentsRow } from './components/CalibrationOverralCommentsRow.tsx'
import { CalibrationRatingRow } from './components/CalibrationRatingRow.tsx'
import { CalibrationUserBox } from './components/CalibrationUserBox.tsx'

export function CalibrationDashboard() {
	const { sessionId, cycleId } = useParams<{ sessionId: string; cycleId: string }>()
	const { showToast } = useToast()
	const queryClient = useQueryClient()

	const {
		data: calibrationData,
		isLoading,
		error,
		refetch,
	} = useQuery({
		...trpc.client.calibrations.getData.queryOptions({ sessionId: sessionId || '' }),
		enabled: !!sessionId,
		retry: 2,
	})

	// State for managing participant selections
	const [participantSelections, setParticipantSelections] = useState<Record<string, boolean>>({})

	// Mutation for submitting calibration ratings
	// @ts-expect-error - TRPC mutation options type compatibility issue
	const submitRatingsMutation = useMutation({
		...trpc.client.calibrations.submitRatings.mutationOptions(),
		onSuccess: () => {
			showToast('Calibrations submitted.')
			// Invalidate and refetch the calibration data to update the UI
			queryClient.invalidateQueries({
				queryKey: ['calibrations', 'getData', { sessionId: sessionId || '' }],
			})
			// Clear all selections after successful submission
			setParticipantSelections({})
		},
		onError: (error: unknown) => {
			showToast('Something went wrong, calibration was not submitted.', 'error')
			console.error('Error submitting ratings:', error)
		},
	})

	// Initialize participant selections when data loads
	useEffect(() => {
		if (calibrationData?.participants) {
			const initialSelections: Record<string, boolean> = {}
			calibrationData.participants.forEach((participant) => {
				initialSelections[participant.id] = participant.selected
			})
			setParticipantSelections(initialSelections)
		}
	}, [calibrationData?.participants])

	// Calculate if all participants are selected
	const allSelected = calibrationData?.participants
		? calibrationData.participants.every((p) => participantSelections[p.id] === true)
		: false

	// Calculate if some (but not all) participants are selected
	const someSelected = calibrationData?.participants
		? calibrationData.participants.some((p) => participantSelections[p.id] === true) && !allSelected
		: false

	// Handler for "Select All" checkbox
	const handleSelectAll = (e: React.ChangeEvent<HTMLInputElement>) => {
		const newSelectionState = e.target.checked
		const newSelections: Record<string, boolean> = {}

		calibrationData?.participants.forEach((participant) => {
			newSelections[participant.id] = newSelectionState
		})

		setParticipantSelections(newSelections)
	}

	// Handler for individual participant selection
	const handleParticipantSelectionChange = (participantId: string, selected: boolean) => {
		setParticipantSelections((prev) => ({
			...prev,
			[participantId]: selected,
		}))
	}

	// Handler for submitting selected participants' ratings
	const handleSubmitSelected = async () => {
		if (!sessionId) {
			console.error('Session ID is required')
			return
		}

		const selectedParticipantIds = Object.keys(participantSelections).filter((id) => participantSelections[id] === true)

		if (selectedParticipantIds.length === 0) {
			showToast('Please select at least one participant.', 'error')
			return
		}

		try {
			await submitRatingsMutation.mutateAsync({
				sessionId,
				participantIds: selectedParticipantIds,
			})
		} catch (error) {
			console.error('Failed to submit ratings:', error)
		}
	}

	// Handler for canceling selections
	const handleCancelSelections = () => {
		setParticipantSelections({})
	}

	// Calculate selected participants count
	const selectedParticipantsCount = Object.values(participantSelections).filter(Boolean).length

	const session = calibrationData?.session

	if (isLoading) {
		return <CalibrationLoading />
	}

	if (error) {
		// @ts-expect-error - Error type compatibility issue
		return <CalibrationError error={error} refetch={refetch} />
	}

	if (!calibrationData || !session) {
		return <CalibrationNotFound />
	}

	console.log(calibrationData)

	// Calculate average ratings for each participant
	const calculateParticipantAverage = (participant: (typeof calibrationData.participants)[0]): number | null => {
		if (!participant.calibratedRatings || participant.calibratedRatings.length === 0) {
			return null
		}

		const validRatings = participant.calibratedRatings
			.map((rating) => rating.numericValue)
			.filter((value): value is number => value !== null && value !== undefined)

		if (validRatings.length === 0) {
			return null
		}

		const sum = validRatings.reduce((acc, value) => acc + value, 0)
		return Math.round((sum / validRatings.length) * 100) / 100 // Round to 2 decimal places
	}

	return (
		<main className='w-full p-6'>
			<ModuleHeader
				breadcrumb={[
					{ label: 'Cycles', to: '/cycles' },
					{ label: session.cycleName || 'Cycle', to: `/cycles/${cycleId}` },
					{ label: 'Calibrations' },
				]}
				title='Calibrations'
			/>

			<section className={'bg-white p-4 border-1 border-neutral-200 rounded-md'}>
				<div className={'my-4 overflow-x-auto'}>
					<div className='flex flex-col min-w-max'>
						<div className='flex items-center'>
							<div className='w-80 p-2 text-left flex-shrink-0 mt-[4%]'>
								<Checkbox
									label={'Select All '}
									checked={allSelected || someSelected}
									isAllSelected={allSelected ? true : someSelected ? false : undefined}
									onChange={handleSelectAll}
								/>
							</div>
							<div className='flex-1 flex flex-col'>
								<h2 className='text-lg font-semibold text-center bg-primary-100 p-4 rounded-sm mb-2'>Direct Reports</h2>
								<div className='flex items-center'>
									{calibrationData.participants.map((participant) => (
										<div key={participant.id} className='w-48 p-2 text-center flex-shrink-0'>
											<CalibrationUserBox
												participant={{
													...participant,
													selected: participantSelections[participant.id] ?? participant.selected,
													calibratedRatings:
														participant.calibratedRatings?.map((rating) => ({
															...rating,
															rowTitle: rating.ratingType || 'Unknown',
														})) || [],
												}}
												onSelectionChange={handleParticipantSelectionChange}
											/>
										</div>
									))}
								</div>
							</div>
						</div>

						{/* Rating Rows - Dynamic rendering based on participants' calibratedRatings */}
						{(() => {
							// Get all unique ratings (by componentId) from all participants
							const allRatings = new Map<string, { componentId: string; ratingType: string }>()

							calibrationData.participants.forEach((participant) => {
								participant.calibratedRatings?.forEach((rating) => {
									if (rating.componentId && rating.ratingType) {
										allRatings.set(rating.componentId, {
											componentId: rating.componentId,
											ratingType: rating.ratingType,
										})
									}
								})
							})

							return Array.from(allRatings.values()).map((rating) => (
								<CalibrationRatingRow
									key={rating.componentId}
									participants={calibrationData.participants.map((participant) => ({
										...participant,
										calibratedRatings:
											participant.calibratedRatings?.map((r) => ({
												...r,
												rowTitle: r.ratingType || 'Unknown',
											})) || [],
									}))}
									rating={{
										componentId: rating.componentId,
										rowTitle: rating.ratingType,
									}}
									ratingElements={calibrationData.ratingElements}
								/>
							))
						})()}

						{/* Comment Rows - Dynamic rendering based on participants' calibrationComments */}
						{(() => {
							// Get unique commentTypes from all participants
							const uniqueCommentTypes = new Set<string>()

							calibrationData.participants.forEach((participant) => {
								participant.calibrationComments?.forEach((comment) => {
									if (comment.commentType) {
										uniqueCommentTypes.add(comment.commentType)
									}
								})
							})

							// Map commentType to display name
							const commentTypeToRowType = {
								accomplishments: 'Accomplishments',
								strengths: 'Strengths',
								improvements: 'Improvements',
							}

							// Only show comment section if we have any comments
							if (uniqueCommentTypes.size === 0) {
								return null
							}

							return (
								<>
									<div className={'p-4 border-neutral-200 border-t bg-neutral-50'}>
										<p>Overall comments</p>
									</div>
									{Array.from(uniqueCommentTypes).map((commentType) => (
										<CalibrationOverralCommentsRow
											key={commentType}
											participants={calibrationData.participants.map((participant) => ({
												...participant,
												calibratedRatings:
													participant.calibratedRatings?.map((r) => ({
														...r,
														rowTitle: r.ratingType || 'Unknown',
													})) || [],
											}))}
											rowType={commentTypeToRowType[commentType as keyof typeof commentTypeToRowType] || commentType}
										/>
									))}
								</>
							)
						})()}

						<div className='flex items-center bg-neutral-50'>
							<div className='w-80 p-4 border-t border-neutral-200 flex-shrink-0 font-bold'>Average</div>
							{calibrationData.participants.map((participant) => {
								const average = calculateParticipantAverage(participant)
								return (
									<div key={participant.id} className='w-48 p-4 border-t border-neutral-200 flex-shrink-0 text-center'>
										{average !== null ? average.toString() : 'N/A'}
									</div>
								)
							})}
						</div>
					</div>
				</div>

				{/* Bulk submission controls */}
				{selectedParticipantsCount > 0 && (
					<div className='flex justify-end items-center gap-4 mt-6 p-4 bg-neutral-50 rounded-md border border-neutral-200'>
						<Button variant='secondary' onClick={handleCancelSelections} disabled={submitRatingsMutation.isPending}>
							Cancel
						</Button>
						<Button onClick={handleSubmitSelected} disabled={submitRatingsMutation.isPending}>
							{submitRatingsMutation.isPending ? 'Submitting...' : `Submit all (${selectedParticipantsCount})`}
						</Button>
					</div>
				)}
			</section>
		</main>
	)
}
